export type AnalyticsMetric =
  | 'bandwidth'
  | 'networkTraffic'
  | 'loss'
  | 'mos'
  | 'jitter'
  | 'latency';

export type AnalyticsSortOrder = 'asc' | 'desc';
export type AnalyticsValueFormat = 'number' | 'bytes' | 'packets' | 'percent' | 'Mbps' | 'ms' | 'MOS';

export interface AnalyticsSortOption {
  label: string;
  value: string;
}

export interface AnalyticsTableColumn {
  key: string;
  label: string;
  format: AnalyticsValueFormat;
}

export interface AnalyticsMetricConfig {
  key: AnalyticsMetric;
  label: string;
  unit: string;
  defaultSort: string;
  defaultOrder: AnalyticsSortOrder;
  primaryField: string;
  seriesField?: string;
  secondaryField?: string;
  secondaryUnit?: string;
  rankingTitle: string;
  timelineTitle: string;
  sortOptions: AnalyticsSortOption[];
  tableColumns: AnalyticsTableColumn[];
}

export interface TopDevicesRequestOptions {
  metric: AnalyticsMetric;
  range?: string;
  from?: string;
  to?: string;
  top: number;
  sort: string;
  order: AnalyticsSortOrder;
  search: string;
  limit: number;
  skip: number;
}

const healthColumns: AnalyticsTableColumn[] = [
  { key: 'loss_pct', label: 'Loss', format: 'percent' },
  { key: 'decode_latency_avg_ms', label: 'Latency', format: 'ms' },
  { key: 'jitter_ms', label: 'Jitter', format: 'ms' },
  { key: 'mos', label: 'MOS', format: 'MOS' }
];

export const TOP_DEVICES_METRIC_CONFIG: { [key in AnalyticsMetric]: AnalyticsMetricConfig } = {
  bandwidth: {
    key: 'bandwidth',
    label: 'Bandwidth',
    unit: 'Mbps',
    defaultSort: 'avg_bandwidth_mbps',
    defaultOrder: 'desc',
    primaryField: 'avg_bandwidth_mbps',
    seriesField: 'bandwidth_mbps',
    secondaryField: 'peak_bandwidth_mbps',
    secondaryUnit: 'Mbps',
    rankingTitle: 'Top Devices by Average Bandwidth',
    timelineTitle: 'Bandwidth Over Time',
    sortOptions: [
      { label: 'Average Bandwidth', value: 'avg_bandwidth_mbps' },
      { label: 'Peak Bandwidth', value: 'peak_bandwidth_mbps' },
      { label: 'Total Data', value: 'total_bytes' },
      { label: 'MAC', value: 'mac' }
    ],
    tableColumns: [
      { key: 'avg_bandwidth_mbps', label: 'Avg Bandwidth', format: 'Mbps' },
      { key: 'peak_bandwidth_mbps', label: 'Peak Bandwidth', format: 'Mbps' },
      { key: 'total_bytes', label: 'Total Data', format: 'bytes' },
      { key: 'tx_bytes', label: 'TX Data', format: 'bytes' },
      { key: 'rx_bytes', label: 'RX Data', format: 'bytes' },
      { key: 'packet_count', label: 'Packet Count', format: 'packets' }
    ]
  },
  networkTraffic: {
    key: 'networkTraffic',
    label: 'Network Traffic',
    unit: 'bytes',
    defaultSort: 'total_bytes',
    defaultOrder: 'desc',
    primaryField: 'total_bytes',
    secondaryField: 'packet_count',
    secondaryUnit: 'packets',
    rankingTitle: 'Top Devices by Total Data',
    timelineTitle: 'Network Traffic Over Time',
    sortOptions: [
      { label: 'Total Data', value: 'total_bytes' },
      { label: 'TX Data', value: 'tx_bytes' },
      { label: 'RX Data', value: 'rx_bytes' },
      { label: 'Packet Count', value: 'packet_count' },
      { label: 'MAC', value: 'mac' }
    ],
    tableColumns: [
      { key: 'total_bytes', label: 'Total Data', format: 'bytes' },
      { key: 'tx_bytes', label: 'TX Data', format: 'bytes' },
      { key: 'rx_bytes', label: 'RX Data', format: 'bytes' },
      { key: 'tx_packets', label: 'TX Packets', format: 'packets' },
      { key: 'rx_packets', label: 'RX Packets', format: 'packets' },
      { key: 'percent_of_total', label: '% of Total', format: 'percent' }
    ]
  },
  loss: healthMetric('loss', 'Loss', '%', 'loss_pct', 'Top Devices by Loss', [
    { key: 'loss_pct', label: 'Loss', format: 'percent' },
    { key: 'decode_latency_avg_ms', label: 'Avg Latency', format: 'ms' },
    { key: 'jitter_ms', label: 'Jitter', format: 'ms' },
    { key: 'mos', label: 'MOS', format: 'MOS' }
  ]),
  mos: healthMetric('mos', 'MOS', 'MOS', 'mos', 'Top Devices by MOS', [
    { key: 'mos', label: 'MOS', format: 'MOS' },
    ...healthColumns.filter(column => column.key !== 'mos')
  ]),
  jitter: healthMetric('jitter', 'Jitter', 'ms', 'jitter_ms', 'Top Devices by Jitter', [
    { key: 'jitter_ms', label: 'Jitter', format: 'ms' },
    ...healthColumns.filter(column => column.key !== 'jitter_ms')
  ]),
  latency: {
    ...healthMetric(
      'latency', 'Latency', 'ms', 'decode_latency_avg_ms', 'Top Devices by Latency', [
        { key: 'decode_latency_avg_ms', label: 'Avg Latency', format: 'ms' },
        { key: 'decode_latency_p90_ms', label: 'P90 Latency', format: 'ms' },
        { key: 'loss_pct', label: 'Loss', format: 'percent' },
        { key: 'jitter_ms', label: 'Jitter', format: 'ms' },
        { key: 'mos', label: 'MOS', format: 'MOS' }
      ]
    ),
    secondaryField: 'decode_latency_p90_ms',
    secondaryUnit: 'ms'
  }
};

export const TOP_DEVICES_METRICS: AnalyticsMetricConfig[] = [
  TOP_DEVICES_METRIC_CONFIG.bandwidth,
  TOP_DEVICES_METRIC_CONFIG.networkTraffic,
  TOP_DEVICES_METRIC_CONFIG.loss,
  TOP_DEVICES_METRIC_CONFIG.mos,
  TOP_DEVICES_METRIC_CONFIG.jitter,
  TOP_DEVICES_METRIC_CONFIG.latency
];

function healthMetric(
  key: AnalyticsMetric,
  label: string,
  unit: string,
  primaryField: string,
  rankingTitle: string,
  tableColumns: AnalyticsTableColumn[]
): AnalyticsMetricConfig {
  return {
    key,
    label,
    unit,
    defaultSort: primaryField,
    defaultOrder: 'desc',
    primaryField,
    rankingTitle,
    timelineTitle: `${label} Over Time`,
    sortOptions: [
      { label: rankingTitle.replace('Top Devices by ', ''), value: primaryField },
      { label: 'MAC', value: 'mac' }
    ],
    tableColumns
  };
}
